import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AsyncBoundary, Icon, Notice, useToast } from '@kollez/design-system';
import { apiGet, apiPost } from '@/lib/api';
import { useUnlockUser, useResendInvite, useSignoutUser } from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';

/**
 * /platform/users/:id — full user profile, standalone shell.
 *
 * Consumes GET /platform/users/:id which returns { user, tenant, activeSessions,
 * recentAudit }. Password / reset tokens are already stripped server-side; every
 * read here writes a cross_tenant.read audit entry.
 *
 * All the actions previously buried inside the Users list drawer are surfaced
 * here as a real action strip: Unlock, Resend invite, Sign-out everywhere, and
 * "View as" (impersonate) behind ticket + reason gates.
 */

interface UserDoc {
  _id: string;
  fullName: string;
  email: string;
  phone?: string;
  role: string;
  tenant?: string;
  status?: string;
  isActive?: boolean;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  onboardingComplete?: boolean;
  hasSigningAuthority?: boolean;
  lastLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
  permissions?: string[];
  mentees?: string[];
  authMethods?: Array<{ kind: string; identityValue: string; verifiedAt?: string; lastUsedAt?: string }>;
  onboardingProgress?: Record<string, unknown>;
  lockedUntil?: string;
  failedLoginAttempts?: number;
}

interface AuditRow {
  _id: string;
  at: string;
  action: string;
  actor?: { userId?: string; role?: string };
  subject?: { kind?: string; id?: string };
  reason?: string;
  meta?: Record<string, unknown>;
}

interface UserDetailResponse {
  user: UserDoc;
  tenant: { _id: string; name: string | null; tenantPath: string | null; tenantType: string | null } | null;
  activeSessions: number;
  recentAudit: AuditRow[];
}

export function PlatformUserDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const [ticket, setTicket] = useState('');
  const [reason, setReason] = useState('');
  const unlock = useUnlockUser();
  const resendInvite = useResendInvite();
  const signout = useSignoutUser();

  const q = useQuery({
    queryKey: ['platform', 'user', id],
    enabled: !!id,
    queryFn: () => apiGet<UserDetailResponse>(`/platform/users/${id}`),
  });

  const impersonate = useMutation({
    mutationFn: () =>
      apiPost<{ accessToken: string; expiresIn: number }>(
        `/platform/users/${id}/impersonate`,
        { reason: reason.trim(), ticket },
      ),
    onSuccess: (r) => {
      sessionStorage.setItem('kollez.access', r.accessToken);
      sessionStorage.setItem(
        'kollez.impersonation',
        JSON.stringify({ target: q.data?.user.fullName, expiresAt: Date.now() + r.expiresIn * 1000 }),
      );
      toast.show({ message: `View-as session started for ${q.data?.user.fullName}. 15 minutes max.` });
      window.location.href = '/home';
    },
    onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
  });

  const u = q.data?.user;
  const t = q.data?.tenant;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">
            <Link to="/platform/users">← All users</Link>
          </p>
          <h1 className="h-page">{u?.fullName ?? 'User'}</h1>
          {u && (
            <p className="subhead">
              {u.role.replace(/_/g, ' ')}
              {t?.name && <> · {t.name}</>}
              {q.data?.activeSessions ? <> · {q.data.activeSessions} active session{q.data.activeSessions === 1 ? '' : 's'}</> : null}
            </p>
          )}
        </div>
      </div>

      <AsyncBoundary
        kind="detail"
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !u}
        emptyTitle="User not found"
        emptyHint="It may have been deleted or you may not have visibility into this tenant."
        onRetry={() => void q.refetch()}
      >
        {u && (
          <>
            <Notice tone="info">
              <Icon name="shield" size={14} />
              Student phone numbers and resumes are hidden in cross-tenant views. Use "View as" with a ticket
              number when you need them. Every read of this page is logged.
            </Notice>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="stack">
                <div className="card card-pad">
                  <h2 className="h-section mb-3">Identity</h2>
                  <dl className="dl">
                    <dt>User ID</dt><dd><code>{u._id}</code></dd>
                    <dt>Email</dt><dd>{u.email}{u.isEmailVerified ? ' ✓' : ' (unverified)'}</dd>
                    <dt>Phone</dt><dd>{u.phone ?? '—'}{u.phone ? (u.isPhoneVerified ? ' ✓' : ' (unverified)') : ''}</dd>
                    <dt>Role</dt><dd>{u.role.replace(/_/g, ' ')}</dd>
                    <dt>Status</dt><dd>{u.status ?? (u.isActive ? 'Active' : 'Inactive')}</dd>
                    {u.lockedUntil && new Date(u.lockedUntil) > new Date() && (
                      <><dt>Locked until</dt><dd className="text-warning">{formatIndianDate(new Date(u.lockedUntil))}</dd></>
                    )}
                    {typeof u.failedLoginAttempts === 'number' && u.failedLoginAttempts > 0 && (
                      <><dt>Failed logins</dt><dd>{u.failedLoginAttempts}</dd></>
                    )}
                    <dt>Onboarding</dt><dd>{u.onboardingComplete ? 'Complete' : 'In progress'}</dd>
                    <dt>Signing authority</dt><dd>{u.hasSigningAuthority ? 'Yes' : 'No'}</dd>
                    <dt>Created</dt><dd>{u.createdAt ? formatIndianDate(new Date(u.createdAt)) : '—'}</dd>
                    <dt>Last seen</dt><dd>{u.lastLoginAt ? formatIndianDate(new Date(u.lastLoginAt)) : 'Never'}</dd>
                  </dl>
                </div>

                {t && (
                  <div className="card card-pad">
                    <h2 className="h-section mb-3">Tenant</h2>
                    <dl className="dl">
                      <dt>Name</dt>
                      <dd>
                        <Link className="link" to={`/platform/tenants`}>{t.name ?? '—'}</Link>
                      </dd>
                      <dt>Type</dt><dd>{t.tenantType ?? '—'}</dd>
                      <dt>Tenant path</dt><dd><code style={{ fontSize: '0.75rem' }}>{t.tenantPath ?? '/'}</code></dd>
                    </dl>
                  </div>
                )}

                {u.permissions && u.permissions.length > 0 && (
                  <div className="card card-pad">
                    <h2 className="h-section mb-3">Permissions ({u.permissions.length})</h2>
                    <div className="flex flex-wrap gap-1">
                      {u.permissions.map((p) => (
                        <span key={p} className="chip text-xs"><code>{p}</code></span>
                      ))}
                    </div>
                  </div>
                )}

                {u.authMethods && u.authMethods.length > 0 && (
                  <div className="card card-pad">
                    <h2 className="h-section mb-3">Auth methods</h2>
                    <div className="tbl-wrap">
                      <table className="tbl">
                        <thead>
                          <tr><th>Kind</th><th>Identity</th><th>Verified</th><th>Last used</th></tr>
                        </thead>
                        <tbody>
                          {u.authMethods.map((a, i) => (
                            <tr key={i}>
                              <td><span className="badge">{a.kind}</span></td>
                              <td className="cell-sub">{a.identityValue}</td>
                              <td className="cell-sub">{a.verifiedAt ? formatIndianDate(new Date(a.verifiedAt)) : '—'}</td>
                              <td className="cell-sub">{a.lastUsedAt ? formatIndianDate(new Date(a.lastUsedAt)) : 'Never'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {q.data?.recentAudit && q.data.recentAudit.length > 0 && (
                  <div className="card card-pad">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="h-section m-0">Recent audit ({q.data.recentAudit.length})</h2>
                      <Link className="link text-sm" to={`/platform/audit?actor=${u._id}`}>Full trail →</Link>
                    </div>
                    <ul className="grid gap-1 text-sm">
                      {q.data.recentAudit.map((a) => (
                        <li key={a._id}>
                          <span className="cell-sub">{formatIndianDate(new Date(a.at))}</span>
                          {' · '}
                          <b>{a.action.replace(/_/g, ' ')}</b>
                          {a.subject?.kind && <> on {a.subject.kind}</>}
                          {a.reason && <> · {a.reason}</>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <aside className="stack">
                <div className="card card-pad">
                  <h2 className="h-section mb-3">Actions</h2>
                  <div className="grid gap-2">
                    <button
                      type="button"
                      className="btn btn-outline"
                      disabled={unlock.isPending || u.status !== 'locked'}
                      title={u.status === 'locked' ? '' : 'Only relevant when the account is locked.'}
                      onClick={() =>
                        unlock.mutate(u._id, {
                          onSuccess: () => {
                            toast.show({ message: `${u.fullName} unlocked.` });
                            void q.refetch();
                          },
                          onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
                        })
                      }
                    >
                      {unlock.isPending ? 'Unlocking…' : 'Unlock account'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      disabled={resendInvite.isPending}
                      onClick={() =>
                        resendInvite.mutate(u._id, {
                          onSuccess: () => toast.show({ message: 'Invitation re-issued.' }),
                          onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
                        })
                      }
                    >
                      <Icon name="send" size={14} />
                      {resendInvite.isPending ? 'Sending…' : 'Resend invitation'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      disabled={signout.isPending || q.data?.activeSessions === 0}
                      onClick={() =>
                        signout.mutate(u._id, {
                          onSuccess: (r) => {
                            toast.show({ message: `Signed out of ${r.revokedCount} session${r.revokedCount === 1 ? '' : 's'}.` });
                            void q.refetch();
                          },
                          onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
                        })
                      }
                    >
                      {signout.isPending ? 'Revoking…' : `Sign out ${q.data?.activeSessions ?? 0} session${q.data?.activeSessions === 1 ? '' : 's'}`}
                    </button>
                  </div>
                </div>

                <div className="card card-pad">
                  <h2 className="h-section mb-3">View as this user</h2>
                  <p className="helper mt-0">
                    15 minutes, hard cap. Their admin is emailed. Every action you take is logged as
                    impersonated.
                  </p>
                  <label className="grid gap-1.5 mt-2">
                    <span className="label">Ticket number</span>
                    <input
                      className="input"
                      placeholder="e.g. #4852"
                      value={ticket}
                      onChange={(e) => setTicket(e.target.value)}
                    />
                  </label>
                  <label className="grid gap-1.5 mt-2">
                    <span className="label">Reason (min 10 chars)</span>
                    <textarea
                      className="input"
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Explain what you're troubleshooting…"
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-primary mt-3"
                    disabled={
                      impersonate.isPending
                      || !/#?\d{3,}/.test(ticket)
                      || reason.trim().length < 10
                    }
                    onClick={() => impersonate.mutate()}
                  >
                    <Icon name="eye" size={14} />
                    {impersonate.isPending ? 'Starting…' : 'Start view-as session'}
                  </button>
                </div>

                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => nav('/platform/users')}
                >
                  ← Back to users list
                </button>
              </aside>
            </div>
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}
